/**
 * Customer-facing invoice preview (same data + layout as the web dashboard invoice).
 * Staff-only: uses orders.getInvoiceDataForInternal.
 */
import { useState, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import { useSession } from "@/lib/auth-client";
import {
  ArrowLeft,
  Download,
  Printer,
  AlertTriangle,
  FileText,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatStatusLabel, isPayWhenReadySentinelDate } from "@/lib/format";
import { getOrderStatusBadgeClass } from "@/lib/status-tag-colors";

// Types for invoice data
interface InvoiceItem {
  id: string;
  name: string;
  origin?: string;
  process?: string;
  unitWeight?: number;
  quantity: number;
  price?: number;
  totalPrice?: number;
}

interface InvoiceTotals {
  subtotal?: number;
  deliveryCharges: number;
  paymentMethodDiscount: number;
  paymentMethodFee: number;
  discountAmount: number;
  pointsDiscount: number;
  pointsRedeemed?: number;
  arCharges?: Array<{
    id: string;
    label: string;
    amount: number;
  }>;
  arChargesTotal?: number;
  total?: number;
}

interface InvoiceUser {
  company?: string;
  name?: string;
  email?: string;
  shippingAddress?: string;
  shippingCity?: string;
  shippingState?: string;
  shippingPostalCode?: string;
  paymentTerms?: number;
}

interface InvoiceOrder {
  _id: string;
  orderNumber?: string;
  createdAt?: number;
  _creationTime?: number;
  status: string;
  paymentMethod?: string;
  paymentStatus?: string;
  appliedPromoCode?: string;
  trackingNumber?: string;
}

interface InvoicePayment {
  paymentMethod?: string;
  paymentStatus?: string;
}

interface ScheduledPayment {
  status: string;
  scheduledDate?: number;
}

interface InvoiceData {
  order: InvoiceOrder;
  user: InvoiceUser | null;
  items: InvoiceItem[];
  totals: InvoiceTotals;
  payment?: InvoicePayment;
  scheduledPayments?: ScheduledPayment[];
}

// Invoice formatting utilities
function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatPaymentMethod(method: string | null | undefined): string {
  if (!method) return "Not specified";

  if (method.startsWith("pm_") || method.startsWith("card_")) {
    return "Credit/Debit Card";
  }

  const lowerMethod = method.toLowerCase();
  if (
    lowerMethod === "us_bank_account" ||
    lowerMethod.includes("bank") ||
    lowerMethod.includes("ach")
  ) {
    return "ACH Bank Transfer";
  }
  if (lowerMethod.includes("paypal")) return "PayPal";
  if (lowerMethod.includes("affirm")) return "Affirm";
  if (lowerMethod.includes("credit")) return "Credit Terms";
  if (lowerMethod.includes("wire")) return "Wire Transfer";
  if (lowerMethod.includes("check")) return "Check";

  return method
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatAddress(user: InvoiceUser | null): string {
  if (!user) return "";

  const parts: string[] = [];
  if (user.shippingAddress) parts.push(user.shippingAddress);
  if (user.shippingCity) parts.push(user.shippingCity);

  const stateZip: string[] = [];
  if (user.shippingState) stateZip.push(user.shippingState);
  if (user.shippingPostalCode) stateZip.push(user.shippingPostalCode);

  if (stateZip.length > 0) parts.push(stateZip.join(" "));

  return parts.join(", ");
}


export default function CustomerInvoicePage() {
  const { id: orderId } = useParams<{ id: string }>();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const { data: session, isPending: isAuthLoading } = useSession();
  const authUserId = (session?.user as { id?: string } | undefined)?.id;

  // Same payload shape as customer web invoice; staff auth via authUserId
  const invoiceData = useQuery(
    api.orders.getInvoiceDataForInternal,
    !isAuthLoading && authUserId && orderId
      ? { orderId: orderId as any, authUserId }
      : "skip",
  ) as InvoiceData | undefined | null;

  const isLoading = Boolean(isAuthLoading || (orderId && authUserId && invoiceData === undefined));
  const hasError = invoiceData === null;

  // Transform invoice data
  const invoice = useMemo(() => {
    if (!invoiceData) return null;
    return {
      order: invoiceData.order,
      user: invoiceData.user,
      items: invoiceData.items,
      totals: invoiceData.totals,
      payment: invoiceData.payment,
      scheduledPayments: invoiceData.scheduledPayments,
    };
  }, [invoiceData]);

  // Calculate due date based on payment terms
  const dueDate = useMemo(() => {
    if (!invoice) return { date: null as number | null, label: "Due Upon Receipt" };

    const { order, user, scheduledPayments, payment } = invoice;

    // 1. If scheduled payment exists and not paid. Pay-when-ready sentinel dates
    // fall through to the customer's payment terms below.
    if (scheduledPayments && scheduledPayments.length > 0) {
      const nextPayment = scheduledPayments.find(
        (p) =>
          p.status === "pending" &&
          p.scheduledDate != null &&
          !isPayWhenReadySentinelDate(p.scheduledDate)
      );
      if (nextPayment?.scheduledDate) {
        return {
          date: nextPayment.scheduledDate,
          label: "Scheduled Payment",
        };
      }
    }

    // 2. If already paid
    const paymentStatus = payment?.paymentStatus || order.paymentStatus;
    if (
      paymentStatus === "paid" ||
      paymentStatus === "completed" ||
      paymentStatus === "succeeded"
    ) {
      return { date: null, label: "PAID" };
    }

    // 3. If user has payment terms (NET 30, etc.)
    if (user?.paymentTerms) {
      const orderDate = new Date(order.createdAt || order._creationTime || Date.now());
      const due = new Date(orderDate);
      due.setDate(due.getDate() + user.paymentTerms);
      return {
        date: due.getTime(),
        label: `NET ${user.paymentTerms}`,
      };
    }

    // 4. Default
    return { date: null, label: "Due Upon Receipt" };
  }, [invoice]);

  // Computed values
  const orderNumber = useMemo(
    () => invoice?.order.orderNumber || invoice?.order._id?.slice(0, 8) || "",
    [invoice]
  );

  const orderDate = useMemo(
    () =>
      invoice
        ? formatDate(invoice.order.createdAt || invoice.order._creationTime || Date.now())
        : "",
    [invoice]
  );

  const paymentMethod = useMemo(
    () =>
      formatPaymentMethod(
        invoice?.payment?.paymentMethod || invoice?.order.paymentMethod
      ),
    [invoice]
  );

  const paymentStatus = useMemo(
    () => invoice?.payment?.paymentStatus || invoice?.order.paymentStatus,
    [invoice]
  );

  const isCancelled = useMemo(
    () => invoice?.order.status?.toLowerCase() === "cancelled",
    [invoice]
  );

  const isPaid = useMemo(
    () =>
      ["paid", "completed", "succeeded"].includes(
        paymentStatus?.toLowerCase() || ""
      ),
    [paymentStatus]
  );

  // Print invoice
  const printInvoice = useCallback(() => {
    window.print();
  }, []);

  // Generate PDF using jspdf
  const generatePDF = useCallback(async () => {
    if (!invoice) return;

    setIsGeneratingPDF(true);

    try {
      const jsPDFModule = await import("jspdf");
      const jsPDF = jsPDFModule.default;

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - margin * 2;
      let y = margin;

      // Row-level formatting utilities
      const addText = (
        text: string,
        x: number,
        yPos: number,
        options: {
          fontSize?: number;
          fontStyle?: "normal" | "bold" | "italic";
          color?: number[];
          align?: "left" | "center" | "right";
          maxWidth?: number;
        } = {}
      ) => {
        pdf.setFontSize(options.fontSize || 10);
        pdf.setFont("helvetica", options.fontStyle || "normal");
        if (options.color) {
          const c = options.color;
          pdf.setTextColor(c[0] || 0, c[1] || 0, c[2] || 0);
        } else {
          pdf.setTextColor(0, 0, 0);
        }
        pdf.text(text, x, yPos, { align: options.align, maxWidth: options.maxWidth });
      };

      const drawLine = (
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        color: number[] = [229, 231, 235]
      ) => {
        pdf.setDrawColor(color[0] || 229, color[1] || 231, color[2] || 235);
        pdf.setLineWidth(0.3);
        pdf.line(x1, y1, x2, y2);
      };

      // Header
      addText("INVOICE", margin, y, { fontSize: 24, fontStyle: "bold" });

      // Status badge (if cancelled)
      if (invoice.order.status === "cancelled") {
        pdf.setFillColor(254, 226, 226);
        pdf.setTextColor(153, 27, 27);
        pdf.roundedRect(margin + 45, y - 6, 25, 8, 1, 1, "F");
        addText("Cancelled", margin + 47, y - 1, { fontSize: 8, color: [153, 27, 27] });
      }

      // Company info
      y += 10;
      addText("Anthem Coffee Imports", margin, y, {
        fontSize: 10,
        fontStyle: "bold",
        color: [75, 85, 99],
      });
      y += 5;
      addText("13615 Wyandotte St., Kansas City, MO 64145", margin, y, {
        fontSize: 9,
        color: [107, 114, 128],
      });

      // Invoice details (right side)
      const rightX = pageWidth - margin;
      addText("Invoice Number", rightX, margin, {
        fontSize: 8,
        color: [107, 114, 128],
        align: "right",
      });
      addText(`#${orderNumber}`, rightX, margin + 6, {
        fontSize: 14,
        fontStyle: "bold",
        align: "right",
      });
      addText(`Date: ${orderDate}`, rightX, margin + 14, {
        fontSize: 9,
        color: [75, 85, 99],
        align: "right",
      });

      // Due date
      if (dueDate.label) {
        const dueText = dueDate.date
          ? `Due: ${formatDate(dueDate.date)} (${dueDate.label})`
          : dueDate.label;
        const dueColor = dueDate.label === "PAID" ? [22, 163, 74] : [0, 0, 0];
        addText(dueText, rightX, margin + 20, {
          fontSize: 9,
          fontStyle: "bold",
          color: dueColor,
          align: "right",
        });
      }

      // Bill To & Payment Details section
      y = 55;
      const boxWidth = contentWidth / 2 - 5;
      const boxHeight = 35;

      // Bill To
      addText("BILL TO", margin, y, { fontSize: 8, fontStyle: "bold", color: [107, 114, 128] });
      const billToBoxY = y + 4;
      pdf.setFillColor(249, 250, 251);
      pdf.roundedRect(margin, billToBoxY, boxWidth, boxHeight, 2, 2, "F");

      // Truncate long payment method labels for compact UI
      const truncate = (text: string, maxLen: number) => {
        if (!text) return "";
        return text.length > maxLen ? text.substring(0, maxLen - 3) + "..." : text;
      };

      let billY = billToBoxY + 6;
      if (invoice.user?.company) {
        addText(truncate(invoice.user.company, 35), margin + 4, billY, {
          fontSize: 10,
          fontStyle: "bold",
        });
        billY += 5;
      }
      if (invoice.user?.name) {
        addText(truncate(invoice.user.name, 40), margin + 4, billY, {
          fontSize: 9,
          color: [55, 65, 81],
        });
        billY += 5;
      }
      if (invoice.user?.shippingAddress) {
        addText(truncate(invoice.user.shippingAddress, 45), margin + 4, billY, {
          fontSize: 8,
          color: [75, 85, 99],
        });
        billY += 4;
      }
      const cityStateZip = [
        invoice.user?.shippingCity,
        invoice.user?.shippingState,
        invoice.user?.shippingPostalCode,
      ]
        .filter(Boolean)
        .join(", ");
      if (cityStateZip) {
        addText(truncate(cityStateZip, 45), margin + 4, billY, {
          fontSize: 8,
          color: [75, 85, 99],
        });
        billY += 4;
      }
      if (invoice.user?.email) {
        addText(truncate(invoice.user.email, 40), margin + 4, billY, {
          fontSize: 8,
          color: [75, 85, 99],
        });
      }

      // Payment Details
      const paymentX = margin + contentWidth / 2 + 5;
      addText("PAYMENT DETAILS", paymentX, y, {
        fontSize: 8,
        fontStyle: "bold",
        color: [107, 114, 128],
      });
      pdf.setFillColor(249, 250, 251);
      pdf.roundedRect(paymentX, billToBoxY, boxWidth, boxHeight, 2, 2, "F");

      let paymentY = billToBoxY + 6;
      addText(`Method: ${paymentMethod}`, paymentX + 4, paymentY, { fontSize: 9 });
      paymentY += 6;
      const statusColor = isPaid ? [22, 163, 74] : [202, 138, 4];
      addText(`Status: ${isPaid ? "Paid" : formatStatusLabel(paymentStatus || "")}`, paymentX + 4, paymentY, {
        fontSize: 9,
        color: statusColor,
      });
      if (invoice.order.trackingNumber) {
        paymentY += 6;
        addText(`Tracking: ${invoice.order.trackingNumber}`, paymentX + 4, paymentY, {
          fontSize: 9,
        });
      }

      // Products Table
      y = billToBoxY + boxHeight + 10;

      // Table header
      pdf.setFillColor(249, 250, 251);
      pdf.rect(margin, y, contentWidth, 10, "F");
      addText("Product", margin + 4, y + 7, {
        fontSize: 9,
        fontStyle: "bold",
        color: [55, 65, 81],
      });
      addText("Qty", margin + contentWidth * 0.55, y + 7, {
        fontSize: 9,
        fontStyle: "bold",
        color: [55, 65, 81],
        align: "center",
      });
      addText("Price/lb", margin + contentWidth * 0.72, y + 7, {
        fontSize: 9,
        fontStyle: "bold",
        color: [55, 65, 81],
        align: "right",
      });
      addText("Total", margin + contentWidth - 4, y + 7, {
        fontSize: 9,
        fontStyle: "bold",
        color: [55, 65, 81],
        align: "right",
      });

      y += 12;
      drawLine(margin, y, margin + contentWidth, y);

      // Table rows
      for (const item of invoice.items) {
        y += 6;
        const rowStartY = y;

        // Product name (truncate if too long)
        const productName = item.name;
        if (!productName) {
          throw new Error("Missing product name for invoice item");
        }
        const truncatedName =
          productName.length > 45 ? productName.substring(0, 42) + "..." : productName;
        addText(truncatedName, margin + 4, y, { fontSize: 9, fontStyle: "bold" });

        // Product details on next line
        const details: string[] = [];
        if (item.origin) details.push(`Origin: ${item.origin}`);
        if (item.unitWeight) details.push(`${item.unitWeight} lbs/bag`);
        if (item.process) details.push(`Process: ${item.process}`);
        if (details.length > 0) {
          y += 4;
          const detailsText = details.join(" | ");
          const truncatedDetails =
            detailsText.length > 60 ? detailsText.substring(0, 57) + "..." : detailsText;
          addText(truncatedDetails, margin + 4, y, { fontSize: 7, color: [107, 114, 128] });
        }

        // Qty, Price, Total - aligned with product name row
        addText(`${item.quantity} bags`, margin + contentWidth * 0.55, rowStartY, {
          fontSize: 9,
          color: [55, 65, 81],
          align: "center",
        });
        addText(`$${(item.price ?? 0).toFixed(2)}`, margin + contentWidth * 0.75, rowStartY, {
          fontSize: 9,
          color: [55, 65, 81],
          align: "right",
        });
        addText(`$${(item.totalPrice ?? 0).toFixed(2)}`, margin + contentWidth - 4, rowStartY, {
          fontSize: 9,
          fontStyle: "bold",
          align: "right",
        });

        y += 6;
        drawLine(margin, y, margin + contentWidth, y, [243, 244, 246]);
      }

      // Totals section
      y += 10;
      const totalsX = margin + contentWidth - 80;
      const totalsWidth = 80;

      const addTotalRow = (label: string, value: string, isGreen = false, isBold = false) => {
        addText(label, totalsX, y, { fontSize: 9, color: [75, 85, 99] });
        addText(value, totalsX + totalsWidth, y, {
          fontSize: isBold ? 11 : 9,
          fontStyle: isBold ? "bold" : "normal",
          color: isGreen ? [22, 163, 74] : [17, 24, 39],
          align: "right",
        });
        y += 6;
      };

      addTotalRow("Subtotal", `$${invoice.totals.subtotal?.toFixed(2) || "0.00"}`);

      if (invoice.totals.deliveryCharges > 0) {
        addTotalRow("Shipping", `$${invoice.totals.deliveryCharges.toFixed(2)}`);
      }
      if (invoice.totals.paymentMethodDiscount > 0) {
        addTotalRow(
          "Bank/ACH Discount (3%)",
          `-$${invoice.totals.paymentMethodDiscount.toFixed(2)}`,
          true
        );
      }
      if (invoice.totals.paymentMethodFee > 0) {
        addTotalRow("PayPal Fee (2%)", `$${invoice.totals.paymentMethodFee.toFixed(2)}`);
      }
      if (invoice.totals.discountAmount > 0) {
        const promoLabel = invoice.order.appliedPromoCode
          ? `Promo (${invoice.order.appliedPromoCode})`
          : "Promo Discount";
        addTotalRow(promoLabel, `-$${invoice.totals.discountAmount.toFixed(2)}`, true);
      }
      if (invoice.totals.pointsDiscount > 0) {
        const ptsLabel = invoice.totals.pointsRedeemed
          ? `Points (${Math.round(invoice.totals.pointsRedeemed)} pts)`
          : "Points Redeemed";
        addTotalRow(ptsLabel, `-$${invoice.totals.pointsDiscount.toFixed(2)}`, true);
      }
      for (const charge of invoice.totals.arCharges ?? []) {
        addTotalRow(charge.label, `$${charge.amount.toFixed(2)}`);
      }

      // Total box
      y += 2;
      pdf.setFillColor(249, 250, 251);
      pdf.roundedRect(totalsX - 4, y - 4, totalsWidth + 8, 12, 2, 2, "F");
      addText("Total", totalsX, y + 4, { fontSize: 11, fontStyle: "bold" });
      addText(`$${invoice.totals.total?.toFixed(2) || "0.00"}`, totalsX + totalsWidth, y + 4, {
        fontSize: 12,
        fontStyle: "bold",
        align: "right",
      });

      // Footer
      y = pageHeight - 25;
      drawLine(margin, y, margin + contentWidth, y);
      y += 8;
      addText("Thank you for your business!", pageWidth / 2, y, {
        fontSize: 10,
        fontStyle: "bold",
        align: "center",
      });
      y += 6;
      addText("Anthem Coffee Imports", pageWidth / 2, y, {
        fontSize: 8,
        color: [107, 114, 128],
        align: "center",
      });

      // Download
      const invoiceNumber = invoice.order.orderNumber || invoice.order._id.slice(0, 8);
      pdf.save(`invoice-${invoiceNumber}.pdf`);
      toast.success("Invoice downloaded successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error(
        `Failed to generate PDF: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    } finally {
      setIsGeneratingPDF(false);
    }
  }, [invoice, orderNumber, orderDate, dueDate, paymentMethod, paymentStatus, isPaid]);

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-[600px] w-full" />
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-600" />
          <h2 className="mb-2 text-xl font-semibold text-red-900">
            Unable to Load Invoice
          </h2>
          <p className="text-red-700">An error occurred while loading the invoice.</p>
          <Link to={`/invoice/${orderId}`}>
            <Button className="mt-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Order
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Not found state
  if (!invoice) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <div className="text-center">
          <FileText className="mx-auto mb-4 h-16 w-16 text-gray-300" />
          <h2 className="mb-2 text-xl font-semibold">Invoice Not Found</h2>
          <p className="text-gray-600">
            The invoice you're looking for doesn't exist.
          </p>
          <Link to="/admin/accounting/orders">
            <Button className="mt-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Orders
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      <div className="mx-auto px-4 py-8 print:p-0">
        {/* Action buttons (hidden in print) */}
        <div className="mx-auto mb-6 flex max-w-[210mm] items-center gap-3 print:hidden">
          <Link
            to={`/invoice/${orderId}`}
            className="inline-flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Order
          </Link>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={printInvoice}>
              <Printer className="mr-2 h-4 w-4" />
              Print
            </Button>
            <Button onClick={generatePDF} disabled={isGeneratingPDF}>
              <Download className="mr-2 h-4 w-4" />
              {isGeneratingPDF ? "Generating..." : "Download PDF"}
            </Button>
          </div>
        </div>

        {/* Invoice content - A4 dimensions */}
        <div className="invoice-content a4-page relative mx-auto bg-white shadow-lg print:shadow-none">
          {/* Cancelled watermark */}
          {isCancelled && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="rotate-[-30deg] border-8 border-red-500 px-8 py-4 text-7xl font-bold uppercase text-red-500 opacity-20">
                Cancelled
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-8 flex flex-col justify-between gap-6 md:flex-row print:flex-row">
            <div>
              <div className="mb-4 flex items-center gap-3">
                <h1 className="text-3xl font-bold text-gray-900">Invoice</h1>
                {invoice.order.status && (
                  <Badge className={getOrderStatusBadgeClass(invoice.order.status)}>
                    {formatStatusLabel(invoice.order.status)}
                  </Badge>
                )}
              </div>
              <p className="text-sm font-medium text-gray-600">Anthem Coffee Imports</p>
              <p className="text-sm text-gray-500">13615 Wyandotte St., Kansas City, MO 64145</p>
            </div>
            <div className="text-left md:text-right print:text-right">
              <p className="text-xs uppercase tracking-wide text-gray-500">Invoice Number</p>
              <p className="text-xl font-bold text-gray-900">#{orderNumber}</p>
              <p className="mt-2 text-sm text-gray-600">Date: {orderDate}</p>
              {dueDate.label && (
                <p
                  className={`mt-1 text-sm font-medium ${
                    dueDate.label === "PAID" ? "text-green-600" : "text-gray-900"
                  }`}
                >
                  {dueDate.date ? (
                    <>
                      Due: {formatDate(dueDate.date)}{" "}
                      <span className="text-xs text-gray-500">({dueDate.label})</span>
                    </>
                  ) : (
                    dueDate.label
                  )}
                </p>
              )}
            </div>
          </div>

          {/* Customer & Payment Info */}
          <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 print:grid-cols-2">
            <div>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Bill To
              </h2>
              <div className="rounded-md border border-gray-100 bg-gray-50 p-4">
                {invoice.user?.company && (
                  <p className="font-semibold text-gray-900">{invoice.user.company}</p>
                )}
                {invoice.user?.name && (
                  <p className="text-gray-700">{invoice.user.name}</p>
                )}
                {formatAddress(invoice.user) && (
                  <p className="mt-2 text-sm text-gray-600">{formatAddress(invoice.user)}</p>
                )}
                {invoice.user?.email && (
                  <p className="mt-1 text-sm text-gray-600">{invoice.user.email}</p>
                )}
              </div>
            </div>
            <div>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Payment Details
              </h2>
              <div className="rounded-md border border-gray-100 bg-gray-50 p-4">
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Method:</span>{" "}
                  <span className="text-gray-900">{paymentMethod}</span>
                </p>
                <p className="mt-1 text-sm">
                  <span className="font-medium text-gray-700">Status:</span>{" "}
                  <span className={isPaid ? "text-green-600" : "text-yellow-600"}>
                    {isPaid ? "Paid" : formatStatusLabel(paymentStatus || "")}
                  </span>
                </p>
                {invoice.order.trackingNumber && (
                  <p className="mt-2 text-sm">
                    <span className="font-medium text-gray-700">Tracking:</span>{" "}
                    <span className="text-gray-900">{invoice.order.trackingNumber}</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Products Table */}
          <div className="mb-8 overflow-hidden rounded-md border border-gray-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 font-semibold text-gray-700">Product</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">Qty</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Price/lb</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoice.items.map((item, itemIndex) => (
                  <tr key={`${item.name}-${itemIndex}`}>
                    <td className="px-4 py-4">
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-gray-500">
                        {item.origin && <span>Origin: {item.origin}</span>}
                        {item.unitWeight && <span>{item.unitWeight} lbs/bag</span>}
                        {item.process && <span>Process: {item.process}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center text-gray-700">
                      {item.quantity} bags
                    </td>
                    <td className="px-4 py-4 text-right text-gray-700">
                      ${item.price?.toFixed(2) || "0.00"}
                    </td>
                    <td className="px-4 py-4 text-right font-medium text-gray-900">
                      ${item.totalPrice?.toFixed(2) || "0.00"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-full max-w-xs space-y-2">
              <div className="flex justify-between border-b border-gray-100 pb-2 text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium text-gray-900">
                  ${invoice.totals.subtotal?.toFixed(2) || "0.00"}
                </span>
              </div>

              {invoice.totals.deliveryCharges > 0 && (
                <div className="flex justify-between border-b border-gray-100 pb-2 text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-medium text-gray-900">
                    ${invoice.totals.deliveryCharges.toFixed(2)}
                  </span>
                </div>
              )}

              {invoice.totals.paymentMethodDiscount > 0 && (
                <div className="flex justify-between border-b border-gray-100 pb-2 text-sm">
                  <span className="text-gray-600">Bank/ACH Discount (3%)</span>
                  <span className="font-medium text-green-600">
                    -${invoice.totals.paymentMethodDiscount.toFixed(2)}
                  </span>
                </div>
              )}

              {invoice.totals.paymentMethodFee > 0 && (
                <div className="flex justify-between border-b border-gray-100 pb-2 text-sm">
                  <span className="text-amber-700">PayPal Fee (2%)</span>
                  <span className="font-medium text-amber-700">
                    ${invoice.totals.paymentMethodFee.toFixed(2)}
                  </span>
                </div>
              )}

              {invoice.totals.discountAmount > 0 && (
                <div className="flex justify-between border-b border-gray-100 pb-2 text-sm">
                  <span className="text-gray-600">
                    Promo Discount
                    {invoice.order.appliedPromoCode && ` (${invoice.order.appliedPromoCode})`}
                  </span>
                  <span className="font-medium text-green-600">
                    -${invoice.totals.discountAmount.toFixed(2)}
                  </span>
                </div>
              )}

              {invoice.totals.pointsDiscount > 0 && (
                <div className="flex justify-between border-b border-gray-100 pb-2 text-sm">
                  <span className="text-gray-600">
                    Points Redeemed
                    {invoice.totals.pointsRedeemed &&
                      ` (${Math.round(invoice.totals.pointsRedeemed)} pts)`}
                  </span>
                  <span className="font-medium text-green-600">
                    -${invoice.totals.pointsDiscount.toFixed(2)}
                  </span>
                </div>
              )}

              {(invoice.totals.arCharges ?? []).map((charge) => (
                <div key={charge.id} className="flex justify-between border-b border-gray-100 pb-2 text-sm">
                  <span className="text-gray-600">{charge.label}</span>
                  <span className="font-medium text-gray-900">
                    ${charge.amount.toFixed(2)}
                  </span>
                </div>
              ))}

              <div className="flex justify-between rounded-md bg-gray-50 px-4 py-3">
                <span className="text-lg font-semibold text-gray-900">Total</span>
                <span className="text-xl font-bold text-gray-900">
                  ${invoice.totals.total?.toFixed(2) || "0.00"}
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-10 border-t border-gray-200 pt-6 text-center">
            <p className="text-sm font-medium text-gray-900">Thank you for your business!</p>
            {!isPaid && (
              <p className="mt-2 text-sm text-amber-700">
                Please contact us if you have any questions about your order.
              </p>
            )}
            <p className="mt-3 text-xs text-gray-500">Anthem Coffee Imports</p>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        /* A4 page dimensions: 210mm x 297mm */
        .a4-page {
          width: 210mm;
          min-height: 297mm;
          padding: 20mm;
          box-sizing: border-box;
        }

        @media screen and (max-width: 900px) {
          .a4-page {
            width: 100%;
            min-height: auto;
            padding: 24px;
          }
        }

        @media print {
          @page {
            margin: 0;
            size: A4;
          }

          .a4-page {
            width: 210mm;
            min-height: 297mm;
            padding: 15mm;
            margin: 0;
            box-shadow: none;
          }

          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Hide non-print elements */
          header,
          footer,
          nav,
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
